import { VOYAGE_STATES, type VoyageSummary } from '@felix/domain';
import type {
  Content,
  CustomTableLayout,
  TableCell,
  TDocumentDefinitions,
} from 'pdfmake/interfaces.js';
import { zonedStamp } from '../../inputs/lt-utc.js';
import { TOKENS } from '../../tokens.js';
import { formatDuration } from './overview.js';
import type { ReportT } from './report-pdf.js';
import type { ReportBrand } from './source.js';
import type { Voyage } from './types.js';

export interface VoyageSummaryPdfInput {
  voyage: Voyage;
  summary: VoyageSummary;
  vessel: { name: string; imo?: string | null };
  brand: ReportBrand | null;
  /** Basım zamanı (UTC ISO). */
  printedAt: string;
}

const LINES: CustomTableLayout = {
  hLineWidth: (i) => (i === 1 ? 0.8 : 0.4),
  vLineWidth: () => 0,
  hLineColor: () => TOKENS.border,
  paddingLeft: () => 4,
  paddingRight: () => 4,
  paddingTop: () => 3,
  paddingBottom: () => 3,
  fillColor: (row) => (row === 0 ? TOKENS['table-head-bg'] : null),
};
const FACTS: CustomTableLayout = { ...LINES, fillColor: () => null, hLineWidth: () => 0.4 };

const num = (value: number | null | undefined, digits = 1) =>
  typeof value === 'number' && Number.isFinite(value) ? value.toFixed(digits) : '—';
const stamp = (iso: string | null) => (iso ? `${zonedStamp(iso)} UTC` : '—');

function logoOf(brand: ReportBrand | null): string | null {
  const logo = brand?.logo ?? null;
  return logo && /^data:image\/(png|jpeg);base64,/.test(logo) ? logo : null;
}

/**
 * Voyage Summary PDF'i (ADR 0082 Ek §2; README S11): A4 dikey, şirket başlığı, sefer ve gemi,
 * süreler, mesafe ve hız, yakıt türüne ve döneme göre tüketim, bacaklar ve dönemler. Tamamlanan
 * seferde "Final", sürende "Provisional" işareti: özet saklanmaz, raporlardan basım anında türetilir.
 * Saf işlev; gemi ve ofis aynı tanımı kullanır (report-pdf.ts kalıbı).
 */
export function voyageSummaryPdf(t: ReportT, input: VoyageSummaryPdfInput): TDocumentDefinitions {
  const { voyage, summary, vessel } = input;
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`voyage.record.summary.${key}`, options);
  const c = (key: string) => t(`voyage.record.columns.${key}`);
  const state = (key: string) => t(`voyage.record.states.${key}`);
  const final = voyage.status === 'completed';
  const logo = logoOf(input.brand);

  const heading = (value: string): Content => ({ text: value, style: 'section' });
  const facts = (pairs: [string, string][]): Content => {
    const body: TableCell[][] = [];
    for (let i = 0; i < pairs.length; i += 2) {
      const [a, b] = [pairs[i], pairs[i + 1]];
      body.push([
        { text: a?.[0] ?? '', style: 'label' },
        { text: a?.[1] ?? '' },
        { text: b?.[0] ?? '', style: 'label' },
        { text: b?.[1] ?? '' },
      ]);
    }
    return { table: { widths: [100, '*', 100, '*'], body }, layout: FACTS };
  };
  const table = (head: string[], body: string[][], widths: (string | number)[]): Content => ({
    table: {
      headerRows: 1,
      widths,
      body: [head.map((h) => ({ text: h, bold: true })), ...body],
    },
    layout: LINES,
  });

  const content: Content[] = [];
  content.push({
    columns: [
      ...(logo ? [{ image: logo, fit: [48, 48] as [number, number], width: 56 }] : []),
      {
        width: '*',
        stack: [
          { text: input.brand?.name ?? '', style: 'company' },
          { text: k('title'), style: 'title' },
          { text: voyage.voyageNo, style: 'muted' },
        ],
      },
      {
        width: 'auto',
        stack: [
          {
            text: final ? k('final') : k('provisional'),
            style: final ? 'final' : 'mark',
            alignment: 'right' as const,
          },
        ],
      },
    ],
    columnGap: 10,
    margin: [0, 0, 0, 8],
  });

  content.push(
    facts([
      [t('voyage.pdf.vessel'), vessel.imo ? `${vessel.name} (IMO ${vessel.imo})` : vessel.name],
      [t('voyage.pdf.voyage'), voyage.voyageNo],
      [t('voyage.pdf.from'), voyage.fromPort],
      [t('voyage.pdf.to'), voyage.toPort],
      [c('start'), stamp(summary.start)],
      [c('end'), stamp(summary.end)],
      [t(`voyage.voyageStatus.${voyage.status}`), final ? k('finalNote') : k('provisionalNote')],
      [k('reports'), String(summary.reportCount)],
    ]),
  );

  const total = summary.hours.total;
  content.push(
    heading(k('time')),
    table(
      [c('period'), c('duration'), c('hours'), '%'],
      [
        ...VOYAGE_STATES.map((s) => [
          state(s),
          formatDuration(summary.hours[s]),
          num(summary.hours[s]),
          total > 0 ? num((summary.hours[s] / total) * 100, 0) : '—',
        ]),
        [k('passageOf'), formatDuration(summary.hours.passage), num(summary.hours.passage), ''],
        [c('total'), formatDuration(total), num(total), total > 0 ? '100' : '—'],
      ],
      ['*', 80, 70, 40],
    ),
    heading(k('distanceSpeed')),
    facts([
      [k('distance'), `${num(summary.distanceNm)} nm`],
      [k('avgSpeed'), summary.avgSpeedKn === null ? '—' : `${num(summary.avgSpeedKn, 2)} kn`],
      [
        k('plannedDistance'),
        voyage.plannedDistanceNm > 0 ? `${num(voyage.plannedDistanceNm, 0)} nm` : '—',
      ],
      [
        k('consumption'),
        summary.consumptionMt === null ? '—' : `${num(summary.consumptionMt, 2)} MT`,
      ],
    ]),
  );

  content.push(heading(k('fuel')));
  content.push(
    summary.fuels.length === 0
      ? { text: k('noFuel'), style: 'muted' }
      : table(
          [c('fuel'), ...VOYAGE_STATES.map((s) => state(s)), c('total')],
          summary.fuels.map((row) => [
            row.fuel,
            ...VOYAGE_STATES.map((s) => num(row.byState[s], 2)),
            num(row.total, 2),
          ]),
          [50, '*', '*', '*', '*', '*', 50],
        ),
  );

  if (summary.legs.length > 0) {
    content.push(
      heading(t('voyage.record.details.legs')),
      table(
        [c('leg'), c('from'), c('to'), c('start'), c('end'), c('distance'), c('duration')],
        summary.legs.map((leg) => [
          String(leg.no),
          leg.from ?? '—',
          leg.to ?? '—',
          stamp(leg.start),
          stamp(leg.end),
          num(leg.distanceNm),
          formatDuration(leg.hours),
        ]),
        [22, '*', '*', 82, 82, 50, 46],
      ),
    );
  }
  if (summary.periods.length > 0) {
    content.push(
      heading(t('voyage.record.details.periods')),
      table(
        [c('period'), c('place'), c('start'), c('end'), c('distance'), c('duration')],
        summary.periods.map((period) => [
          state(period.state),
          period.place ?? '—',
          stamp(period.start),
          stamp(period.end),
          num(period.distanceNm),
          formatDuration(period.hours),
        ]),
        [60, '*', 82, 82, 50, 46],
      ),
    );
  }
  content.push({ text: k('derivedNote'), style: 'muted', margin: [0, 10, 0, 0] });

  const printed = t('voyage.pdf.printed', { at: `${zonedStamp(input.printedAt)} UTC` });
  return {
    pageSize: 'A4',
    pageOrientation: 'portrait',
    pageMargins: [36, 36, 36, 44],
    defaultStyle: { font: 'Roboto', fontSize: 9, color: TOKENS['navy-900'] },
    info: {
      title: `${k('title')} ${voyage.voyageNo}`,
      subject: vessel.name,
      creator: 'Felixmate',
    },
    styles: {
      company: { fontSize: 10, bold: true, color: TOKENS['text-secondary'] },
      title: { fontSize: 15, bold: true, margin: [0, 2, 0, 2] },
      section: { fontSize: 10.5, bold: true, margin: [0, 10, 0, 4] },
      label: { color: TOKENS['text-secondary'] },
      muted: { color: TOKENS['text-secondary'], fontSize: 8 },
      mark: { bold: true, color: TOKENS['warning-fg'], fontSize: 9 },
      final: { bold: true, color: TOKENS['success-fg'], fontSize: 9 },
    },
    content,
    footer: (page: number, pages: number) => ({
      columns: [
        {
          text: `${voyage.voyageNo} · ${printed} · Felixmate`,
          fontSize: 7,
          color: TOKENS['text-secondary'],
        },
        {
          text: t('voyage.pdf.page', { page, pages }),
          alignment: 'right',
          fontSize: 7,
          color: TOKENS['text-secondary'],
        },
      ],
      margin: [36, 12, 36, 0],
    }),
  };
}

/** Dosya adı: "VYG-2026-014 Voyage Summary.pdf". */
export function voyageSummaryPdfFileName(voyageNo: string, title: string): string {
  return `${`${voyageNo} ${title}`.replace(/[^A-Za-z0-9 ._-]+/g, '-')}.pdf`;
}
